import type {
    CreateMlroCaseRequest,
    CreateMlroCaseResponse,
    CreateMlroCaseWireRequest,
    CreateMlroCaseWireResponse,
    MlroAlertListResponse,
    MlroAlertListWire,
    MlroAlertSeverity,
    MlroAlertSummary,
    MlroAlertSummaryWire,
    MlroCase,
    MlroCaseAlertLinkWire,
    MlroCaseAttachedAlert,
    MlroCaseAttachment,
    MlroCaseAttachmentWire,
    MlroCaseDetailWire,
    MlroCaseListResponse,
    MlroCaseListWire,
    MlroCaseNote,
    MlroCaseNoteRequest,
    MlroCaseNoteResponse,
    MlroCaseNoteWire,
    MlroCaseNoteWireRequest,
    MlroCaseNoteWireResponse,
    MlroCaseStatusRequest,
    MlroCaseStatusWireRequest,
    MlroCaseSummary,
    MlroCaseWire,
    MlroSeverityWire,
} from '@/types/mlro'

// Maps blockpot-service's MLRO payloads to the console shapes and back. The cases handler
// (`internal/httpapi/mlro_cases_handler.go`) and the alert record (`internal/mlro/query.go`)
// speak snake_case and store severity as `warn`; the alerts list
// (`internal/httpapi/mlro_alerts_handler.go`) already speaks camelCase. The service is the
// source of truth (BLO-946): nothing here invents a field the service does not send.

export function severityFromWire(wire: MlroSeverityWire): MlroAlertSeverity {
    return wire === 'warn' ? 'warning' : wire
}

export function severityToWire(severity: MlroAlertSeverity): MlroSeverityWire {
    return severity === 'warning' ? 'warn' : severity
}

export function mapMlroCase(wire: MlroCaseWire): MlroCaseSummary {
    return {
        caseId: wire.case_id,
        openedAt: wire.opened_at,
        openedByAddress: wire.opened_by ?? null,
        title: wire.title,
        severity: severityFromWire(wire.severity),
        status: wire.status,
        subjectType: wire.subject_type,
        subjectId: wire.subject_id,
        summary: wire.summary,
        closedAt: wire.closed_at ?? null,
        closedByAddress: wire.closed_by ?? null,
        closingNote: wire.closing_note ?? null,
    }
}

export function mapMlroCaseList(wire: MlroCaseListWire): MlroCaseListResponse {
    return {
        cases: (wire.cases ?? []).map(mapMlroCase),
        // Not omitempty: the last page sends "".
        nextCursor: wire.next_cursor || null,
    }
}

function mapAttachment(wire: MlroCaseAttachmentWire): MlroCaseAttachment {
    return { name: wire.filename, url: wire.url, contentType: wire.mime }
}

function attachmentToWire(a: MlroCaseAttachment): MlroCaseAttachmentWire {
    return {
        filename: a.name,
        ...(a.url ? { url: a.url } : {}),
        ...(a.contentType ? { mime: a.contentType } : {}),
    }
}

export function mapMlroCaseNote(wire: MlroCaseNoteWire): MlroCaseNote {
    return {
        noteId: wire.note_id,
        writtenAt: wire.written_at,
        writtenByAddress: wire.written_by,
        body: wire.body,
        attachments: (wire.attachments ?? []).map(mapAttachment),
    }
}

export function mapMlroCaseAlertLink(wire: MlroCaseAlertLinkWire): MlroCaseAttachedAlert {
    const rec = wire.alert
    return {
        alertId: wire.alert_id,
        attachedAt: wire.attached_at,
        alert: rec
            ? {
                  category: rec.category,
                  severity: severityFromWire(rec.severity),
                  summary: rec.summary,
                  raisedAt: rec.fired_at,
              }
            : null,
    }
}

export function mapMlroCaseDetail(wire: MlroCaseDetailWire): MlroCase {
    return {
        ...mapMlroCase(wire.case),
        notes: (wire.notes ?? []).map(mapMlroCaseNote),
        attachedAlerts: (wire.alerts ?? []).map(mapMlroCaseAlertLink),
    }
}

export function createCaseToWire(body: CreateMlroCaseRequest): CreateMlroCaseWireRequest {
    return {
        title: body.title,
        severity: severityToWire(body.severity),
        subject_type: body.subjectType,
        subject_id: body.subjectId,
        summary: body.summary,
        ...(body.attachAlertIds ? { attach_alert_ids: body.attachAlertIds } : {}),
    }
}

export function mapCreateCaseResponse(wire: CreateMlroCaseWireResponse): CreateMlroCaseResponse {
    return { caseId: wire.case_id, failedAttachments: wire.failed_attachments ?? [] }
}

export function noteToWire(body: MlroCaseNoteRequest): MlroCaseNoteWireRequest {
    return {
        body: body.body,
        ...(body.attachments ? { attachments: body.attachments.map(attachmentToWire) } : {}),
    }
}

export function mapNoteResponse(wire: MlroCaseNoteWireResponse): MlroCaseNoteResponse {
    return { noteId: wire.note_id }
}

export function statusToWire(body: MlroCaseStatusRequest): MlroCaseStatusWireRequest {
    return {
        status: body.status,
        ...(body.closingNote !== undefined ? { closing_note: body.closingNote } : {}),
    }
}

export function mapMlroAlert(wire: MlroAlertSummaryWire): MlroAlertSummary {
    return {
        alertId: wire.alertId,
        raisedAt: wire.raisedAt,
        category: wire.category,
        severity: wire.severity,
        subjectType: wire.subjectType,
        subjectId: wire.subjectId,
        summary: wire.summary,
        acknowledgedAt: wire.acknowledgedAt,
        acknowledgedBy: wire.acknowledgedBy,
        caseId: wire.caseId,
    }
}

export function mapMlroAlertList(wire: MlroAlertListWire): MlroAlertListResponse {
    return { alerts: (wire.alerts ?? []).map(mapMlroAlert), nextCursor: wire.nextCursor }
}
